import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";
import * as React from "react";

import { cn } from "~/lib/utils";

/**
 * Gradient colors used for the Generate button and similar CTAs.
 * Orange → Red/Coral → Purple
 */
const GRADIENT_BORDER =
  "bg-gradient-to-r from-[rgb(255,138,101)] via-[rgb(245,85,49)] via-35% to-[rgb(164,90,223)]";

/** Accent color from the gradient for icons */
export const GRADIENT_ACCENT_COLOR = `text-[#F55531]`;

const gradientButtonVariants = cva(
  "relative inline-flex items-center justify-center rounded-md text-sm font-medium transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50",
  {
    variants: {
      size: {
        default: "h-9 px-4 py-2",
        sm: "h-8 rounded-md px-3 text-xs",
        lg: "h-10 rounded-md px-8",
      },
      variant: {
        outline: "",
        filled: "",
      },
    },
    defaultVariants: {
      size: "default",
      variant: "outline",
    },
  },
);

export interface GradientButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof gradientButtonVariants> {
  asChild?: boolean;
}

/**
 * A button with a gradient border.
 *
 * - `outline` (default): White background, gradient border/text. Fills with gradient on hover.
 * - `filled`: Gradient background, white text. Shows white background on hover.
 *
 * Usage:
 * ```tsx
 * <GradientButton>
 *   <GradientButtonIcon><SparklesIcon className="w-4 h-4" /></GradientButtonIcon>
 *   Generate
 * </GradientButton>
 *
 * <GradientButton variant="filled">
 *   <GradientButtonIcon><SparklesIcon className="w-4 h-4" /></GradientButtonIcon>
 *   AI Generate
 * </GradientButton>
 * ```
 */
const GradientButton = React.forwardRef<HTMLButtonElement, GradientButtonProps>(
  ({ className, size, variant = "outline", asChild = false, children, ...props }, ref) => {
    const Comp = asChild ? Slot : "button";
    const isFilled = variant === "filled";

    return (
      <Comp
        className={cn(
          gradientButtonVariants({ size, variant }),
          "p-[1px]",
          GRADIENT_BORDER,
          "group",
          isFilled && "gradient-filled",
          className,
        )}
        ref={ref}
        {...props}
      >
        <span
          className={cn(
            "flex items-center justify-center gap-2 w-full h-full rounded-[5px] px-4 py-1.5",
            "transition-all duration-200",
            isFilled
              ? "bg-transparent group-hover:bg-white"
              : "bg-white group-hover:bg-transparent",
          )}
        >
          <span
            className={cn(
              "flex items-center justify-center gap-2",
              "font-medium",
              isFilled
                ? "text-white group-hover:text-[#F55531]"
                : cn(GRADIENT_ACCENT_COLOR, "group-hover:text-white group-hover:bg-none"),
            )}
          >
            {children}
          </span>
        </span>
      </Comp>
    );
  },
);
GradientButton.displayName = "GradientButton";

/**
 * Icon wrapper for use inside GradientButton.
 * Displays a gradient-colored icon that turns white on button hover.
 */
const GradientButtonIcon = React.forwardRef<
  HTMLSpanElement,
  React.HTMLAttributes<HTMLSpanElement>
>(({ className, children, ...props }, ref) => {
  return (
    <span
      ref={ref}
      className={cn("relative flex items-center justify-center", className)}
      {...props}
    >
      {/* Default state: gradient-colored icon (white when parent is filled) */}
      <span
        className={cn(
          GRADIENT_ACCENT_COLOR,
          "group-[.gradient-filled]:text-white",
          "group-hover:opacity-0 transition-opacity duration-200",
        )}
      >
        {children}
      </span>
      {/* Hover state: swaps color (white for outline, gradient for filled) */}
      <span
        className={cn(
          "absolute inset-0 flex items-center justify-center opacity-0 transition-opacity duration-200",
          "text-white group-hover:opacity-100",
          "group-[.gradient-filled]:text-[#F55531]",
        )}
      >
        {children}
      </span>
    </span>
  );
});
GradientButtonIcon.displayName = "GradientButtonIcon";

export { GradientButton, GradientButtonIcon, gradientButtonVariants };
